/* eslint-disable max-len */
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { Box, Button, Grid, MenuItem, Select, SelectChangeEvent, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { useRef } from 'react';
import { DocumentHyperlink } from '../../components/DocumentHyperlink';
import { MainContainer } from "../../components/MainContainerComp";
import ScrollIndicatorsComp from '../../components/ScrollIndicatorsComp/ScrollIndicatorsComp';
import { WorkInfoTable } from "../../components/WorkInfoTable";
import useFixedPositionButton from '../../utils/hooks/useFixedPositionButton';
import useVerticalScroll from '../../utils/hooks/useVerticalScroll';
import { IWorkInformation } from './WorkInfoData';
import { COASTWISE_CATEGORIES, Category, REGIONS, Region, SQUAMISH_CATEGORIES, VANCOUVER_CATEGORIES, getRows } from "./WorkInfoModels";
import { asteriskStyle, brochureButtonStyle, casualMandateNormalStyle, fixedSectionStyle, fixedStateContentWrapper, lastUpdateStyle, selectContainerItemStyle, selectContainerStyle, selectStyle } from "./styled";

interface IWorkInfoProps {
  data: IWorkInformation;
  hasAll: boolean;
  selectedRegion: Region;
  selectedCategory: Category;
  handleChangeRegion: (event: SelectChangeEvent<Region>) => void;
  handleChangeCategory: (event: SelectChangeEvent<Category>) => void;
  handleViewBrochure: () => void;
  handleDeclareAvailability: () => void;
}

export const WorkInfo: React.FC<IWorkInfoProps> = ({
  data: {
    workInformation,
    totals,
    lastUpdate
  },
  hasAll,
  selectedCategory,
  selectedRegion,
  handleChangeCategory,
  handleChangeRegion,
  handleViewBrochure,
  handleDeclareAvailability
}) => {
  const rows = getRows(workInformation, hasAll, totals);
  const notLocalCategories = selectedRegion === Region.squamish ? SQUAMISH_CATEGORIES : COASTWISE_CATEGORIES;
  const categories = selectedRegion === Region.vancouver ? VANCOUVER_CATEGORIES : notLocalCategories;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { positionStyle } = useFixedPositionButton(containerRef);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const sectionFixedAtTop = useVerticalScroll();
  // const casualMandateStyle: SxProps = shouldFixCasualMandate ? casualMandateFixedStyle(theme) : ;
  return (
    <MainContainer sx={{ pb: 6 }} hasGreyBg>
      <Box sx={sectionFixedAtTop ? fixedSectionStyle : undefined}>
        <Box sx={{ display: 'inline-block', width: '100%' }}>
          {selectedRegion === Region.vancouver &&
            <DocumentHyperlink
              dataAutomation='van-dispatch-pdf-link'
              onClick={handleViewBrochure}
              title='Vancouver Dispatch Brochure'
              styles={brochureButtonStyle}
            />
          }
        </Box>
        <Box sx={casualMandateNormalStyle}>
          <Typography>Casual Mandate Being Enforced</Typography>
          <Typography>For more information please contact your ILWU Coordinators.</Typography>
        </Box>
        <Grid sx={selectContainerStyle} justifyContent="center" container>
          <Grid sx={selectContainerItemStyle} item xs={6}>
            <Typography data-automation="region-lbl">Region<Typography component={"span"} sx={asteriskStyle}>&nbsp;*</Typography></Typography>
            <Select
              className='small'
              data-automation="region-dropdown-value"
              sx={selectStyle}
              value={selectedRegion}
              onChange={handleChangeRegion}
            >
              {REGIONS.map((region) => (
                <MenuItem key={region} value={region} data-automation={`dropdown-region-${Object.keys(Region)[REGIONS.indexOf(region)]}`}>
                  {region}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid sx={selectContainerItemStyle} item xs={6}>
            <Typography data-automation="category-lbl">Category<Typography component={"span"} sx={asteriskStyle}>&nbsp;*</Typography></Typography>
            <Select
              className='small'
              data-automation="category-dropdown-value"
              sx={selectStyle}
              value={selectedCategory}
              onChange={handleChangeCategory}
              MenuProps={{
                style: { maxHeight: isMobile ? 300 : undefined }
              }}
            >
              {categories.map((category) => (
                <MenuItem key={category} value={category} data-automation={`dropdown-category-${Object.keys(Category)[categories.indexOf(category)]}`}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid sx={selectContainerItemStyle} item xs={12}>
            <Typography data-automation="last-update-label" sx={lastUpdateStyle}>Last Update: {lastUpdate}</Typography>
          </Grid>
        </Grid>
      </Box>
      <Box sx={sectionFixedAtTop ? fixedStateContentWrapper : undefined}>
        {selectedCategory === Category.all &&
          <Typography
            sx={(theme) => ({ ...lastUpdateStyle(theme), mb: { xs: 0.5, sm: { xs: 0.5, sm: 2 } } })}
          >
            {Category.all}
          </Typography>}
        <Typography
          component={'div'}
          variant='container'
          data-automation="data-container"
        >
          <ScrollIndicatorsComp ref={containerRef}>
            <WorkInfoTable {...rows} />
          </ScrollIndicatorsComp>
        </Typography>
        <>
          {selectedCategory === Category.all && categories.filter(c => c !== Category.all).map((category) => {
            const workInfoByCategory = workInformation.filter(wi => wi.section === category);
            const categoryRows = getRows(workInfoByCategory, false, totals);
            return (<Box id={`${category.replace(/\s+/g, '-')}`} key={category}>
              <Typography sx={(theme) => ({ ...lastUpdateStyle(theme), my: { xs: 0.5, sm: 2 } })}>{category}</Typography>
              <Typography
                component={'div'}
                variant='container'
                data-automation="data-container"
              >
                <ScrollIndicatorsComp ref={containerRef}>
                  <WorkInfoTable {...categoryRows} />
                </ScrollIndicatorsComp>
              </Typography>
            </Box>);
          })}
        </>
        <Button
          sx={{ fontSize: { xs: '0.85rem', sm: '16px' }, p: { xs: '3px 16px', sm: '6px 16px' } }}
          variant="oval"
          data-automation="declare-availability-button"
          style={positionStyle}
          onClick={handleDeclareAvailability}
        >
          <EventAvailableIcon />
          DECLARE
        </Button>
      </Box>
    </MainContainer>
  )
}
